import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  alpha,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Devices as DevicesIcon,
  DirectionsCar as VehicleIcon,
  Description as DocumentIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  GpsFixed as GpsIcon,
  Settings as SettingsIcon,
  PersonOutline as PersonIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Warning as WarningIcon,
  FormatListBulleted as ListIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

// Import contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useThemeContext } from './contexts/ThemeContext';

// Import components
import ProtectedRoute from './components/ProtectedRoute';
import GroupedNavigation from './components/GroupedNavigation';

// Import pages
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import DevicesPage from './pages/DevicesPage';
import SimsPage from './pages/SimsPage';
import ProvidersPage from './pages/ProvidersPage';
import VehiclesPage from './pages/VehiclesPage';
// ContractsPage removed - using documents (proforma/invoice) instead
import DocumentsPage from './pages/DocumentsPage';
import CoverageHistoryPage from './pages/CoverageHistoryPage';
import LoginPage from './pages/LoginPage';
import ClientPortalPage from './pages/ClientPortalPage';
import ClientProfilePage from './pages/ClientProfilePage';
import SettingsPage from './pages/SettingsPage';
import PDFTemplateEditorPage from './pages/PDFTemplateEditorPage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import ConflictsPage from './pages/ConflictsPage';
import DevicePricesPage from './pages/DevicePricesPage';
import DocumentTypesPage from './pages/DocumentTypesPage';
import ClientDocumentsPage from './pages/ClientDocumentsPage';
import InformationTypesPage from './pages/InformationTypesPage';
import PredefinedLinesPage from './pages/PredefinedLinesPage';
import VisibilitySettingsPage from './pages/VisibilitySettingsPage';
import IMEIConflictsPage from './pages/IMEIConflictsPage';

const drawerWidth = 220;

// Admin menu items
const adminMenuItems = [
  { text: 'Tableau de Bord', icon: <DashboardIcon />, path: '/' },
  { text: 'Clients', icon: <PeopleIcon />, path: '/clients' },
  { text: 'Dispositifs GPS', icon: <DevicesIcon />, path: '/devices' },
  { text: 'Cartes SIM', icon: <GpsIcon />, path: '/sims' },
  { text: 'Opérateurs', icon: <SettingsIcon />, path: '/providers' },
  { text: 'Véhicules', icon: <VehicleIcon />, path: '/vehicles' },
  { text: 'Historique', icon: <HistoryIcon />, path: '/history' },
  { text: 'Documents', icon: <DocumentIcon />, path: '/documents' },
  { text: 'Lignes Prédéfinies', icon: <DocumentIcon />, path: '/predefined-lines' },
  { text: 'Templates PDF', icon: <DocumentIcon />, path: '/pdf-templates' },
  { text: 'Entreprise', icon: <SettingsIcon />, path: '/company-settings' },
  { text: 'Visibilité', icon: <VisibilityIcon />, path: '/visibility-settings' },
  { text: 'Conflits', icon: <WarningIcon />, path: '/conflicts' },
  { text: 'Prix Dispositifs', icon: <DevicesIcon />, path: '/device-prices' },
  { text: 'Types Documents', icon: <DocumentIcon />, path: '/document-types' },
  { text: 'Docs Clients', icon: <DocumentIcon />, path: '/client-documents' },
  { text: 'Types Infos', icon: <SettingsIcon />, path: '/information-types' },
  { text: 'Synchronisation', icon: <SettingsIcon />, path: '/settings' },
];

// Client menu items (limited access)
const clientMenuItems = [
  { text: 'Mon Portail', icon: <DashboardIcon />, path: '/' },
];

// Navigation item component with active state
function NavItem({ item, isActive }) {
  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        component={Link}
        to={item.path}
        selected={isActive}
        sx={{
          borderRadius: 2,
          mx: 1.5,
          color: 'grey.300',
          '& .MuiListItemIcon-root': {
            color: 'grey.400',
          },
          '&:hover': {
            bgcolor: alpha('#fff', 0.08),
          },
          '&.Mui-selected': {
            bgcolor: alpha('#fff', 0.12),
            '& .MuiListItemIcon-root': {
              color: 'primary.light',
            },
            '& .MuiListItemText-primary': {
              color: 'white',
              fontWeight: 600,
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
        <ListItemText 
          primary={item.text} 
          primaryTypographyProps={{ fontSize: '0.9rem' }}
        />
      </ListItemButton>
    </ListItem>
  );
}

// Main layout component with sidebar and navbar
function MainLayout() {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode, branding, sidebarColor } = useThemeContext();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);

  // Determine if user is admin or client
  const isAdmin = user?.is_staff;
  const isClient = user?.is_client;
  const menuItems = isAdmin ? adminMenuItems : clientMenuItems;

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Modern App Bar */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: darkMode ? 'background.paper' : 'white',
          borderBottom: '1px solid',
          borderColor: darkMode ? 'grey.800' : 'grey.200',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {branding.logo ? (
              <Box
                component="img"
                src={branding.logo}
                alt={branding.company_name}
                sx={{ height: 40, mr: 2 }}
              />
            ) : (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  background: `linear-gradient(135deg, ${branding.theme.light.primary} 0%, ${branding.theme.light.secondary} 100%)`,
                  borderRadius: 2,
                  p: 1,
                  mr: 2,
                }}
              >
                <GpsIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
            )}
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                }}
              >
                {branding.company_name}
              </Typography>
              {isClient && user?.client?.company_name && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {user.client.company_name}
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Dark Mode Toggle */}
            <Tooltip title={darkMode ? 'Mode clair' : 'Mode sombre'}>
              <IconButton onClick={toggleDarkMode} color="inherit" sx={{ color: 'text.primary' }}>
                {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            
            <Chip
              label={isAdmin ? 'Admin' : 'Client'}
              size="small"
              color={isAdmin ? 'primary' : 'success'}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Box 
              onClick={handleMenu}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                p: 1,
                borderRadius: 2,
                '&:hover': { bgcolor: darkMode ? 'grey.800' : 'grey.100' },
              }}
            >
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36, 
                  bgcolor: isAdmin ? 'primary.main' : 'success.main',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                {(user?.first_name?.[0] || user?.username?.[0] || 'U').toUpperCase()}
              </Avatar>
              <Box sx={{ ml: 1.5, display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {user?.first_name || user?.username}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {user?.email}
                </Typography>
              </Box>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: { width: 220, mt: 1 }
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleClose}>
                <PersonIcon sx={{ mr: 1.5, fontSize: 20 }} />
                Mon Profil
              </MenuItem>
              <MenuItem onClick={handleClose}>
                <SettingsIcon sx={{ mr: 1.5, fontSize: 20 }} />
                Paramètres
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} />
                Déconnexion
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Modern Sidebar Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: sidebarColor,
            borderRight: '1px solid',
            borderColor: darkMode ? 'grey.800' : 'grey.200',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'hidden', py: 2 }}>
          <Typography 
            variant="overline" 
            sx={{ 
              px: 3, 
              color: 'grey.400',
              fontWeight: 600,
              letterSpacing: 1,
            }}
          >
            Navigation
          </Typography>
          {isAdmin ? (
            <GroupedNavigation />
          ) : (
            <List sx={{ mt: 1 }}>
              {menuItems.map((item) => (
                <NavItem 
                  key={item.text} 
                  item={item} 
                  isActive={
                    item.path === '/' 
                      ? location.pathname === '/' 
                      : location.pathname.startsWith(item.path)
                  }
                />
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: 3,
          bgcolor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {isAdmin ? (
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:id" element={<ClientProfilePage />} />
            <Route path="/devices" element={<DevicesPage />} />
            <Route path="/sims" element={<SimsPage />} />
            <Route path="/providers" element={<ProvidersPage />} />
            <Route path="/vehicles" element={<VehiclesPage />} />
                        <Route path="/history" element={<CoverageHistoryPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/predefined-lines" element={<PredefinedLinesPage />} />
            <Route path="/pdf-templates" element={<PDFTemplateEditorPage />} />
            <Route path="/company-settings" element={<CompanySettingsPage />} />
            <Route path="/visibility-settings" element={<VisibilitySettingsPage />} />
            <Route path="/conflicts" element={<ConflictsPage />} />
            <Route path="/device-prices" element={<DevicePricesPage />} />
            <Route path="/document-types" element={<DocumentTypesPage />} />
            <Route path="/client-documents" element={<ClientDocumentsPage />} />
            <Route path="/information-types" element={<InformationTypesPage />} />
            <Route path="/imei-conflicts" element={<IMEIConflictsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        ) : (
          <Routes>
            <Route path="/" element={<ClientPortalPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </Box>
    </Box>
  );
}

// Public route wrapper - redirects to dashboard if already logged in
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return null;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            
            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
